'use client';
import {
  type Components,
  createTheme as createMuiTheme,
  type Theme,
} from '@mui/material/styles';

import type { SettingsState } from '@infor/core/models/auth';
import { components } from '@infor/ui-lib/src/theme/core';
import { setMobileSafeArea } from '@infor/utils-capacitor/src/mobile-safe-area';

import { customShadows, mixins, palette, typography } from './core';
import { shadows } from './core/shadows';
import { themeConfig } from './theme-config';
import type { ThemeOptions } from './types';
import { updateComponentsWithSettings } from './with-settings/update-components';
import { updateCoreWithSettings } from './with-settings/update-core';

export const baseTheme: ThemeOptions = {
  colorSchemes: {
    light: {
      palette: palette.light,
      shadows: shadows.light,
      customShadows: customShadows.light,
    },
    dark: {
      palette: palette.dark,
      shadows: shadows.dark,
      customShadows: customShadows.dark,
    },
  },
  mixins,
  components,
  typography,
  shape: { borderRadius: 8 },
  direction: themeConfig.direction,
  cssVariables: themeConfig.cssVariables,
  defaultColorScheme: themeConfig.defaultMode,
  appSafeArea: {
    safeArea: {
      top: `var(--safe-area-inset-top)`,
      right: 'var(--safe-area-inset-right)',
      bottom: `calc(var(--safe-area-inset-bottom) + 16px)`,
      left: 'var(--safe-area-inset-left)',
    },
    safeAreaWithOffset: {
      top: 'calc(var(--safe-area-inset-top + 64px))',
      right: 'var(--safe-area-inset-right)',
      bottom: 'calc(var(--safe-area-inset-bottom + 64px))',
      left: 'var(--safe-area-inset-left)',
    },
  },
};

// ----------------------------------------------------------------------

type CreateThemeProps = {
  settingsState?: SettingsState;
  themeOverrides?: ThemeOptions;
  localeComponents?: { components?: Components<Theme> };
};

export function createTheme({
  settingsState,
  themeOverrides = {},
  localeComponents = {},
}: CreateThemeProps = {}): Theme {
  void setMobileSafeArea({ androidEdgeToEdge: true });
  // Update core theme settings
  const updatedCore = settingsState
    ? updateCoreWithSettings(baseTheme, settingsState)
    : baseTheme;

  // Update component settings
  const updatedComponents = settingsState
    ? updateComponentsWithSettings(components, settingsState)
    : {};

  // Create and return the final theme
  return createMuiTheme(
    updatedCore,
    updatedComponents,
    localeComponents,
    themeOverrides
  );
}
